import React from "react";
import { Box, Button } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { SubscriptionStatusEnum } from "@rentablo/api/apiv1";
import { useDownloadSubscription } from "@api/v1/hooks/useSubscription";
import { getCashbackSubscriptionUrl } from "@helpers/mainAppUrls";
import { createObjectUrl, downloadFile } from "@helpers/files";

interface CashbackDashboardItemControlsProps {
  status: SubscriptionStatusEnum | "cancelled";
  subscriptionId: number;
}

interface getComponentArgs {
  status: SubscriptionStatusEnum | "cancelled";
  downloadPdf: (id: number) => void;
  id: number;
}

interface BaseBtnProps {
  downloadPdf: (id: number) => void;
  id: number;
}

const ControlsWrapper = styled(Box)`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  padding-top: ${({ theme }) => theme.spacing(3)};
`;

const Primary = (props: BaseBtnProps) => {
  const { id } = props;
  const { t } = useTranslation();
  return (
    <Button
      size="small"
      variant="primary"
      href={getCashbackSubscriptionUrl(id)}
    >
      {t("domains.cashback.dashboard.item.edit")}
    </Button>
  );
};

const Pdf = (props: BaseBtnProps) => {
  const { downloadPdf, id } = props;
  const { t } = useTranslation();
  return (
    <Button size="small" variant="tertiary" onClick={() => downloadPdf(id)}>
      {t("domains.cashback.dashboard.item.pdf")}
    </Button>
  );
};

const getComponent = (getComponentArgs: getComponentArgs) => {
  const { status, downloadPdf, id } = getComponentArgs;
  switch (status) {
    case "incomplete":
      return (
        <ControlsWrapper>
          <Primary id={id} downloadPdf={downloadPdf} />
        </ControlsWrapper>
      );
    case "in_progress":
      return (
        <ControlsWrapper>
          <Primary id={id} downloadPdf={downloadPdf} />
          <Pdf downloadPdf={downloadPdf} id={id} />
        </ControlsWrapper>
      );
    case "internal_processing":
      return (
        <ControlsWrapper>
          <Pdf downloadPdf={downloadPdf} id={id} />
        </ControlsWrapper>
      );
    case "processing_at_bank":
      return (
        <ControlsWrapper>
          <Pdf downloadPdf={downloadPdf} id={id} />
        </ControlsWrapper>
      );
    case "processed":
      return null;
    case "contract_terminated":
      return null;
    default:
      return null;
  }
};

export const CashbackDashboardItemControls = (
  props: CashbackDashboardItemControlsProps
) => {
  const { status, subscriptionId } = props;
  const { mutate } = useDownloadSubscription({
    onSuccess: (data) => {
      const url = createObjectUrl(data);
      downloadFile(url, "cashback-buchung");
    },
  });
  const downloadPdf = (id: number) => mutate(id);
  return <>{getComponent({ status, downloadPdf, id: subscriptionId })}</>;
};
